import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Permission, UserRole } from '@tutor/shared';
import { LoadingState } from '@tutor/ui';
import { useAuthStore } from '../stores/auth.store';

export interface ProtectedRouteProps {
  children: ReactNode;
  /** Restricts the route to these roles. Omit to allow any signed-in user. */
  role?: UserRole | UserRole[];
  permission?: Permission | Permission[];
}

/**
 * Route protection for the *user experience* only.
 *
 * It stops someone landing on a page they cannot use and being met with a wall of
 * errors. It is not a security boundary: every API call behind these routes is
 * authorised again on the server, which is what actually keeps a student out of
 * another student's lesson.
 */
export function ProtectedRoute({ children, role, permission }: ProtectedRouteProps) {
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const permissions = useAuthStore((state) => state.permissions);
  const homeRoute = useAuthStore((state) => state.homeRoute);
  const location = useLocation();

  if (status === 'idle' || status === 'restoring') {
    return (
      <div className="flex h-full min-h-screen items-center justify-center">
        <LoadingState label="Checking your session…" />
      </div>
    );
  }

  if (status !== 'authenticated' || !user) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    if (!allowed.includes(user.role)) return <Navigate to={homeRoute()} replace />;
  }

  if (permission) {
    const required = Array.isArray(permission) ? permission : [permission];
    if (!required.every((p) => permissions.includes(p))) return <Navigate to={homeRoute()} replace />;
  }

  return <>{children}</>;
}

/** Keeps a signed-in user off /login and /forgot-password. */
export function PublicOnlyRoute({ children }: { children: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const homeRoute = useAuthStore((state) => state.homeRoute);

  if (status === 'idle' || status === 'restoring') {
    return (
      <div className="flex h-full min-h-screen items-center justify-center">
        <LoadingState label="Loading…" />
      </div>
    );
  }

  if (status === 'authenticated') return <Navigate to={homeRoute()} replace />;

  return <>{children}</>;
}
